import { useState } from "react";
import { DatePicker, Form, Input, Modal, Upload, message } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import type { Dayjs } from "dayjs";

import type {
  NewVersionFormValues,
  StudyDocumentRow,
} from "@/views/DocumentWorkspace/documentWorkspace.types";

interface FormShape {
  amendmentLabel?: string;
  effectiveFrom?: Dayjs;
  note?: string;
}

/**
 * Upload a new version of an existing document.
 *
 * Separate from the new-document upload because it is a different act: this one supersedes
 * the current version rather than starting a lineage. For a site-scoped document the new
 * version starts implemented nowhere — each site adopts it after its own approval — so no
 * effective date is asked for per site here.
 */
export function NewVersionModal({
  document,
  onCancel,
  onSubmit,
}: {
  document: StudyDocumentRow | null;
  onCancel: () => void;
  onSubmit: (draft: NewVersionFormValues) => void;
}) {
  const [form] = Form.useForm<FormShape>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const isSiteScoped = document?.rollout_scope === "site";

  const resetState = () => {
    form.resetFields();
    setFileList([]);
  };

  return (
    <Modal
      open={!!document}
      title={`New version — ${document?.title ?? ""}`}
      okText="Upload version"
      width={560}
      onCancel={() => {
        resetState();
        onCancel();
      }}
      onOk={async () => {
        const file = fileList[0]?.originFileObj;
        if (!file) {
          message.error("Choose a file to upload.");
          return;
        }
        let values: FormShape;
        try {
          values = await form.validateFields();
        } catch {
          return;
        }
        onSubmit({
          amendmentLabel: values.amendmentLabel?.trim() || null,
          effectiveFrom: values.effectiveFrom
            ? values.effectiveFrom.format("YYYY-MM-DD")
            : null,
          note: values.note?.trim() || null,
          file,
        });
        resetState();
      }}
      destroyOnHidden
    >
      <p className="mb-4 text-[12px] text-[#6B7280]">
        This becomes v{(document?.current_version ?? 0) + 1} and supersedes v
        {document?.current_version ?? 0}, which is retained because records
        reference it.
      </p>

      <Upload.Dragger
        className="mb-4"
        multiple={false}
        maxCount={1}
        fileList={fileList}
        beforeUpload={() => false}
        onChange={({ fileList: next }) => setFileList(next.slice(-1))}
        onRemove={() => setFileList([])}
      >
        <p className="text-[13px] font-medium text-[#1F2933]">
          Drop the new version here, or browse
        </p>
      </Upload.Dragger>

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="amendmentLabel" label="Amendment / version identity">
          <Input placeholder="e.g. Amendment 3, or v4.0" />
        </Form.Item>

        <Form.Item
          name="effectiveFrom"
          label={isSiteScoped ? "Sponsor approval date" : "Effective from"}
          rules={[{ required: true, message: "A date is required." }]}
          extra={
            isSiteScoped
              ? "Sites adopt it individually after their own IRB/EC approval, so it is in force nowhere until they do."
              : undefined
          }
        >
          <DatePicker className="w-full" format="YYYY-MM-DD" />
        </Form.Item>

        <Form.Item name="note" label="What changed?">
          <Input.TextArea
            rows={2}
            placeholder="e.g. Updated the KRI thresholds."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
